import { useEffect, useRef, type MutableRefObject } from "react";
import { addIProovEventBreadcrumb } from "../../shared/utils/sentryBreadcrumbs";
import {
  trackIProovFailure,
  trackIProovError,
  trackIProovUnsupported,
  trackIProovCanceled,
  trackIProovPermissionDenied,
} from "../../shared/utils/sentryTracking";
import { trackViewed } from "../../shared/analytics/events";
import { DOCUMENT_CAPTURE_EVENTS, DOCUMENT_CAPTURE_SECTIONS, type OutcomeSection } from "../../shared/analytics/documentCaptureEvents";
import {
  hasExhaustedPermissionRecovery,
  hasExhaustedAnyPermissionRecovery,
  recordPermissionRecoveryAttempt,
  clearAllPermissionRecovery,
} from "../../shared/utils/permissionRecovery";
import type { FaceVerificationErrorPayload } from "../../shared/types";
import type { FaceVerificationState, BlockedPermission, VerificationFailureContext } from "./useFaceVerification";
import { logger } from "../../shared/utils/logger";

export interface ConsentFailure {
  isTimeout: boolean;
  message: string;
}

/**
 * iProov `failed` codes that mean the scan was judged and rejected — a biometric verdict.
 * Anything else, including a code we haven't seen, is a fault: an unlisted verdict
 * under-reports rejections where an unlisted fault used to inflate them. Ported from the
 * `failure_*` keys in src/locales/iproov/en.json, minus the nine that never judged anyone
 * (system_error, timeout, processing_fault, device_issue, motion_issue, device_restart,
 * unknown, network_problem, user_not_found).
 */
const BIOMETRIC_VERDICT_CODES = new Set([
  "background_issue",
  "eyes_closed",
  "eyewear",
  "face_not_found",
  "face_too_close",
  "face_too_far",
  "frames_blurry",
  "lighting_issues",
  "misaligned_face",
  "multiple_faces",
  "obscured_face",
  "rejected",
  "sunglasses",
  "too_bright",
  "too_dark",
  "too_much_movement",
]);

interface UseIProovEventsParams {
  iproovRef: React.RefObject<HTMLElement>;
  iproovToken: string | null;
  verificationId: string | null;
  sessionToken: string | null;
  /**
   * Advance the flow — the analytics section, and the view state when the two move
   * together. Owned by `useFaceVerification`, which holds both.
   */
  setFlowStage: (section: OutcomeSection, next?: FaceVerificationState) => void;
  setProgress: (progress: { percent: number; message: string }) => void;
  onVerificationPassed: () => Promise<void>;
  onVerificationError: (
    errorCode: FaceVerificationErrorPayload["errorCode"],
    message: string,
    feedback?: string,
    context?: VerificationFailureContext,
  ) => Promise<void>;
  /**
   * Camera/motion permission was denied — enter the recovery screen instead of
   * hard-erroring to the parent (VEX-7599). The host-page reload the recovery
   * screen offers is the only reliable way to re-prompt on iOS Safari.
   */
  onPermissionBlocked: (permissionType: BlockedPermission) => void;
  /**
   * Set when WE cancelled the transaction (teardown, or the consent-failure path), so
   * handleError can tell our own cancel from a real one. Without it, cancelling on
   * unmount fires `error_transaction_canceled` and we report our own teardown as a
   * liveness failure.
   */
  selfCancelledRef?: MutableRefObject<boolean>;
  /**
   * A capture failed. Stay on iProov's own `failed` slot — which renders every reason
   * the event carries — instead of hard-erroring to the parent, which would tear the
   * iframe down (VEX-7653). Whether the member may retry is the host's call.
   */
  onCaptureFailed?: (feedbacks: string[], message: string) => void | Promise<void>;
  consentFailureRef: MutableRefObject<ConsentFailure | null>;
}

/**
 * Custom hook to manage iProov web component event listeners
 *
 * Handles all iProov events:
 * - ready: iProov is initialized and ready
 * - started: Face scanning has begun
 * - streaming: Video streaming started
 * - progress: Progress updates during scanning
 * - passed: Biometric verification succeeded
 * - failed: Verification failed
 * - error: An error occurred
 * - canceled: User canceled the process
 * - unsupported: Browser/device not supported
 * - permission: Requesting camera permission
 * - permission_denied: Camera or motion permission denied (which one is in reasons[0].feedback_code)
 */
export function useIProovEvents({
  iproovRef,
  iproovToken,
  verificationId,
  sessionToken,
  setFlowStage,
  setProgress,
  onVerificationPassed,
  onVerificationError,
  onPermissionBlocked,
  selfCancelledRef,
  onCaptureFailed,
  consentFailureRef,
}: UseIProovEventsParams) {
  // Latches iOS's DeviceMotion prompt so a later `permission_denied` can tell motion
  // from camera. A ref, not an effect-local `let`: an effect re-run (its deps include
  // callback identities) would reset the flag mid-prompt and misclassify the denial.
  const motionPermissionRequested = useRef(false);

  // Capture-entry latch, re-armed on each `ready`. A ref, not an effect-local `let`: a
  // re-run mid-capture (deps include callback identities) would double-emit the capture view.
  const captureEntryEmitted = useRef(false);

  // Same latch for the stream-live milestone: iProov can re-fire `streaming` within one
  // transaction, and the funnel wants one row per capture.
  const streamStartedEmitted = useRef(false);

  useEffect(() => {
    if (!iproovRef.current || !iproovToken) return;

    const iproovElement = iproovRef.current;

    const handleReady = () => {
      logger.log("[iProov] Ready");

      // Analytics: info-screen view on `ready`
      trackViewed(DOCUMENT_CAPTURE_EVENTS.VIEWED.INFO_SCREEN, "section", { section: DOCUMENT_CAPTURE_SECTIONS.INFO });
      setFlowStage(DOCUMENT_CAPTURE_SECTIONS.INFO);
      captureEntryEmitted.current = false;
      streamStartedEmitted.current = false;

      // Track successful initialization
      addIProovEventBreadcrumb('ready', verificationId);
    };

    const handleStarted = () => {
      logger.log("[iProov] Started");

      // Analytics: capture entry, not the take-photo tap — a member who cancels the iOS
      // motion prompt never gets here, so their journey must not file against this screen.
      setFlowStage(DOCUMENT_CAPTURE_SECTIONS.CAPTURE, "capturing");

      // Analytics: capture-screen view on `started` (fullscreen capture launched)
      if (!captureEntryEmitted.current) {
        trackViewed(DOCUMENT_CAPTURE_EVENTS.VIEWED.FACE_CAPTURE_SCREEN, "section", { section: DOCUMENT_CAPTURE_SECTIONS.CAPTURE });
        captureEntryEmitted.current = true;
      }

      // Track scan started
      addIProovEventBreadcrumb('started', verificationId);
    };

    const handleStreaming = () => {
      logger.log("[iProov] Streaming");

      // Analytics: the camera is delivering frames. `started` (the capture view above) only
      // says iProov opened fullscreen — a camera that never streams still fires it, so the
      // gap between the two is the only place that failure is visible. Not a stage change:
      // the journey is already in `capture`, and this milestone concludes nothing.
      if (!streamStartedEmitted.current) {
        trackViewed(DOCUMENT_CAPTURE_EVENTS.VIEWED.CAMERA_STREAM_STARTED, "indicator", { section: DOCUMENT_CAPTURE_SECTIONS.CAPTURE });
        streamStartedEmitted.current = true;
      }

      // Track streaming state
      addIProovEventBreadcrumb('streaming', verificationId);
    };

    const handleProgress = (event: any) => {
      const { progress, message } = event.detail;
      logger.log("[iProov] Progress:", progress, message);
    };

    const handlePassed = async () => {
      // Track successful verification
      addIProovEventBreadcrumb('passed', verificationId);

      // Transition to processing and poll backend for final verification result
      // Analytics: processing-screen view on the genuine pass→processing transition
      trackViewed(DOCUMENT_CAPTURE_EVENTS.VIEWED.PROCESSING_SCREEN, "section", { section: DOCUMENT_CAPTURE_SECTIONS.PROCESSING });
      setFlowStage(DOCUMENT_CAPTURE_SECTIONS.PROCESSING, "processing");

      // A capture got through — don't let an earlier denial count against a later one.
      clearAllPermissionRecovery();

      await onVerificationPassed();
    };

    const handleFailed = async (event: any) => {
      // v6: failure reasons arrive in `reasons[]`. We localize via our iProov
      // `language` JSON (failure_<code> keys), so `localized_description` is
      // already in the user's language — read it directly. We use reasons[0]
      // (the primary reason). `feedback_code` is the bare code (e.g. "eyes_closed");
      // the backend re-derives its own failure_/error_ taxonomy server-side.
      const { reasons = [] } = event.detail;
      const primary = reasons[0];
      const feedbackCode = primary?.feedback_code || "unknown";
      const description = primary?.localized_description || "Verification failed";

      // A verdict means the member completed the scan and iProov judged it. This handler is
      // the only place that can tell a verdict from a fault, so it reports which happened and
      // lets the error path — the journey's single outcome filer — decide what to file.
      const scanCompleted = BIOMETRIC_VERDICT_CODES.has(feedbackCode);

      trackIProovFailure(feedbackCode, description, event.detail, verificationId, sessionToken);

      // Hold on iProov's own `failed` slot rather than tearing the iframe down. Their
      // slot renders every reason the v6 event carries (up to three, each a distinct
      // instruction); posting VERIFICATION_ERROR here would destroy the iframe and the
      // member would see only reasons[0] re-rendered by the host. VEX-7653.
      // Guarded on having at least one code. The new feedback-code system returns an EMPTY
      // list when a transaction fails early (where the legacy one returned ["unknown"]), and
      // iProov inject the reasons into their own slot — so holding here with nothing to
      // inject would show the member an empty card above a Try again button. Falling through
      // instead reports through the error path, where `feedbackCode` is already "unknown"
      // and the host renders failure_unknown as it did before.
      const feedbacks = reasons.map((r: any) => r?.feedback_code).filter(Boolean);

      if (onCaptureFailed && feedbacks.length > 0) {
        logger.log(`[iProov] failed with ${feedbacks.length} reason(s)`);
        await onCaptureFailed(feedbacks, description);
        return;
      }

      await onVerificationError("face_liveness_failed", description, feedbackCode, { scanCompleted });
    };

    const handleError = async (event: any) => {
      logger.error("[iProov] Error:", event.detail);
      const { reasons = [] } = event.detail;
      const primary = reasons[0];
      const feedbackCode = primary?.feedback_code ?? "unknown";
      const description = primary?.localized_description ?? "iProov error occurred";

      // Our own teardown cancel, not a member action or a real fault. iProov fires
      // `error` with this code whenever cancelTransaction() runs — including on a
      // finished transaction — so without this guard every unmount produces a spurious
      // liveness failure and a spurious Sentry event. Checked AFTER the consent branch,
      // which sets the same flag but wants its own reporting.
      if (feedbackCode === "error_transaction_canceled" && !consentFailureRef.current && selfCancelledRef?.current) {
        logger.log("[iProov] Ignoring error_transaction_canceled from our own teardown");
        return;
      }

      if (feedbackCode === "error_transaction_canceled" && consentFailureRef.current) {
        const { message } = consentFailureRef.current;
        consentFailureRef.current = null;
        await onVerificationError("consent_post_api_failed", message, "consent_failed");
        return;
      }

      trackIProovError(description, feedbackCode, event.detail, verificationId, sessionToken);

      await onVerificationError("face_liveness_failed", description, feedbackCode);
    };

    const handleCanceled = async () => {
      trackIProovCanceled(verificationId, sessionToken);
      await onVerificationError("user_canceled", "User cancelled verification", "user_canceled");
    };

    const handleUnsupported = async (event: any) => {
      // iProov says WHY in reasons[0].feedback_code: browser below its minimum
      // version, no WASM, no fullscreen, no video input, blocked localStorage, or a
      // missing accelerometer/gyroscope/magnetometer permissions policy. This used to
      // hardcode `error_device_motion_unsupported`, so every one of those reached IVA
      // as a device-motion failure — the observed mislabel was an iOS 15.1 browser
      // (below iProov's 15.2 floor) that never even ran the gyro test (VEX-7631).
      //
      // `error_not_supported` ("the device or integration isn't able to run the Web
      // SDK") is iProov's own generic code, so the fallback stays inside their
      // taxonomy rather than inventing a reason we didn't observe.
      const { reasons = [] } = event?.detail ?? {};
      const feedbackCode: string = reasons[0]?.feedback_code || "error_not_supported";

      trackIProovUnsupported(verificationId, sessionToken, feedbackCode, event?.detail);

      // Message text left as-is deliberately: it renders in the member-facing error
      // state, so swapping in iProov's localized_description is a copy change.
      await onVerificationError("face_liveness_failed", "Device or browser not supported", feedbackCode);
    };

    const handlePermission = () => {
      // DeviceMotion permission prompts only occur on iOS; on other platforms
      // iProov fires `permission` for camera access, not device motion. Latch it so
      // a later permission_denied can distinguish motion from camera (VEX-7599).
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) motionPermissionRequested.current = true;

      // Analytics: `permission` fires at startup, before `ready`. Open that stage so a journey
      // ending here (an exhausted denial) files against this screen; `ready` just moves it on.
      trackViewed(DOCUMENT_CAPTURE_EVENTS.VIEWED.CAMERA_PERMISSION_SCREEN, "section", { section: DOCUMENT_CAPTURE_SECTIONS.CAMERA_PERMISSION });
      setFlowStage(DOCUMENT_CAPTURE_SECTIONS.CAMERA_PERMISSION);

      addIProovEventBreadcrumb('permission', verificationId);
    };

    const handlePermissionDenied = async (event: any) => {
      // Analytics: a denial IS the permission gate, whichever stage the flow had reached — on
      // iOS the motion prompt fires no second `permission` event to move the stage for us.
      setFlowStage(DOCUMENT_CAPTURE_SECTIONS.CAMERA_PERMISSION);

      // VEX-7599: enter the recovery screen rather than hard-erroring (which tears the
      // iframe down). Its Reload button reloads the host page — the only reliable
      // re-prompt on iOS Safari, where the decision is scoped to the document load.
      //
      // VEX-7631: iProov names the permission in reasons[0].feedback_code, so read it
      // rather than guessing. The UA latch below it stays only as a fallback for when
      // iProov sends no reasons: on its own it classified EVERY iOS denial as motion
      // (any iOS `permission` event sets the latch), so iOS camera denials — the
      // majority case in in-app browsers — were recorded as motion denials.
      const { reasons = [] } = event?.detail ?? {};
      const feedbackCode: string | undefined = reasons[0]?.feedback_code;

      // Whether iProov named the gate themselves. Only their code is stable across the
      // recovery reload; the latch below is rebuilt from scratch in the new JS context.
      const authoritative =
        feedbackCode === "error_device_motion_denied" ||
        feedbackCode === "error_camera_permission_denied";

      const permissionType: BlockedPermission =
        feedbackCode === "error_device_motion_denied"
          ? "motion"
          : feedbackCode === "error_camera_permission_denied"
            ? "camera"
            : motionPermissionRequested.current
              ? "motion"
              : "camera";

      logger.error(`[iProov] ${permissionType === "motion" ? "Device motion/orientation" : "Camera"} permission denied`);
      trackIProovPermissionDenied(verificationId, sessionToken, permissionType, feedbackCode, event?.detail);

      // Analytics: a RECOVERABLE denial files no outcome — the journey continues through the
      // host reload. The exhausted branch below hands back to the error path, which files one.

      // Once a reload has been tried and the denial persists (OS/site-level block, which
      // no reload clears), fall back to the error path — the recovery screen posts no
      // VERIFICATION_ERROR, so staying on it means never escalating to trusted referee.
      //
      // A guessed classification checks BOTH gates. The allowance is keyed by gate, and
      // the fallback can resolve the same persistent denial differently either side of
      // the reload — so trusting it per-gate hands out a second allowance and reloads the
      // member twice. Erring toward escalation is right here: on the cohort that reaches
      // this path the reload usually cannot clear the denial anyway.
      if (authoritative ? hasExhaustedPermissionRecovery(permissionType) : hasExhaustedAnyPermissionRecovery()) {
        logger.warn(`[iProov] ${permissionType} recovery exhausted — handing back to the error path`);
        clearAllPermissionRecovery();
        await onVerificationError(
          permissionType === "motion" ? "device_motion_denied" : "camera_permission_denied",
          permissionType === "motion" ? "Device motion access was denied" : "Camera access was denied",
          // Prefer iProov's own code; only synthesize one when they sent no reasons.
          feedbackCode || (permissionType === "motion" ? "error_device_motion_denied" : "error_camera_permission_denied")
        );
        return;
      }

      recordPermissionRecoveryAttempt(permissionType);
      onPermissionBlocked(permissionType);
    };

    // Add event listeners
    iproovElement.addEventListener("ready", handleReady);
    iproovElement.addEventListener("started", handleStarted);
    iproovElement.addEventListener("streaming", handleStreaming);
    iproovElement.addEventListener("progress", handleProgress);
    iproovElement.addEventListener("passed", handlePassed);
    iproovElement.addEventListener("failed", handleFailed);
    iproovElement.addEventListener("error", handleError);
    iproovElement.addEventListener("canceled", handleCanceled);
    iproovElement.addEventListener("unsupported", handleUnsupported);
    iproovElement.addEventListener("permission", handlePermission);
    iproovElement.addEventListener("permission_denied", handlePermissionDenied);

    // Cleanup
    return () => {
      iproovElement.removeEventListener("ready", handleReady);
      iproovElement.removeEventListener("started", handleStarted);
      iproovElement.removeEventListener("streaming", handleStreaming);
      iproovElement.removeEventListener("progress", handleProgress);
      iproovElement.removeEventListener("passed", handlePassed);
      iproovElement.removeEventListener("failed", handleFailed);
      iproovElement.removeEventListener("error", handleError);
      iproovElement.removeEventListener("canceled", handleCanceled);
      iproovElement.removeEventListener("unsupported", handleUnsupported);
      iproovElement.removeEventListener("permission", handlePermission);
      iproovElement.removeEventListener("permission_denied", handlePermissionDenied);
    };
  }, [iproovToken, verificationId, sessionToken, iproovRef, setFlowStage, setProgress, onVerificationPassed, onVerificationError, onPermissionBlocked]);
}
